/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import Chart from "react-apexcharts";
import { PrimaryButton } from "../buttons/PrimaryButton";
import dayjs from "dayjs";
// @ts-ignore
import { useScreenshot } from "use-react-screenshot";
import { useDashboardStats } from "../../store/useDashboardStats";
import { useMarketPricing } from "../../store/useMarketPricing";
import {
  Menu,
  MenuHandler,
  MenuItem,
  MenuList,
  Tooltip,
} from "@material-tailwind/react";
import { ReactComponent as ChevronDown } from "../../assets/icons/chevrons/chevron-down.svg";
import InformationIcon from "../../assets/icons/info/information.png";

export const MaterialConversion = React.memo(() => {
  /** Refs */
  const snapshotRef = useRef(null);
  /** Hooks */
  const { materialConversion, getMaterialConversion } = useDashboardStats();
  const { marketPrices, getAllMarketPrices } = useMarketPricing();

  const [_, takeScreenshot] = useScreenshot();
  /** State */
  const [startYear, setStartYear] = useState(() => {
    const currentDate = dayjs();
    return currentDate.subtract(6, "month").year();
  });
  const [startMonth, setStartMonth] = useState(() => {
    const currentDate = dayjs();
    return currentDate.subtract(6, "month").month() + 1; // dayjs months are 0-indexed
  });
  const [endYear, setEndYear] = useState(dayjs().year());
  const [endMonth, setEndMonth] = useState(dayjs().month() + 1); // dayjs months are 0-indexed
  const [selectedMaterial, setSelectedMaterial] = useState<string>("");
  const [selectedMaterialId, setSelectedMaterialId] = useState<string>("");

  const yearOptions = Array.from(
    { length: dayjs().year() - 2023 },
    (_, i) => dayjs().year() - i
  );

  /** Month options */
  const startMonthOptions = useMemo(() => {
    const currentYear = dayjs().year();
    const currentMonth = dayjs().month() + 1;
    const months = [
      { value: 1, label: "Jan" },
      { value: 2, label: "Feb" },
      { value: 3, label: "Mar" },
      { value: 4, label: "Apr" },
      { value: 5, label: "May" },
      { value: 6, label: "Jun" },
      { value: 7, label: "Jul" },
      { value: 8, label: "Aug" },
      { value: 9, label: "Sep" },
      { value: 10, label: "Oct" },
      { value: 11, label: "Nov" },
      { value: 12, label: "Dec" },
    ];

    if (startYear === currentYear) {
      return months.filter((month) => month.value <= currentMonth);
    }
    return months;
  }, [startYear]);

  const endMonthOptions = useMemo(() => {
    const currentYear = dayjs().year();
    const currentMonth = dayjs().month() + 1;
    const months = [
      { value: 1, label: "Jan" },
      { value: 2, label: "Feb" },
      { value: 3, label: "Mar" },
      { value: 4, label: "Apr" },
      { value: 5, label: "May" },
      { value: 6, label: "Jun" },
      { value: 7, label: "Jul" },
      { value: 8, label: "Aug" },
      { value: 9, label: "Sep" },
      { value: 10, label: "Oct" },
      { value: 11, label: "Nov" },
      { value: 12, label: "Dec" },
    ];

    if (endYear === currentYear) {
      return months.filter((month) => month.value <= currentMonth);
    }
    return months;
  }, [endYear]);

  const materialOptions = useMemo(() => {
    const kgOptions = marketPrices?.length
      ? marketPrices?.filter((mp) => mp.measurement === "kg")
      : [];
    return kgOptions?.length
      ? kgOptions?.map((price) => ({
          id: price._id,
          name: price.name,
        }))
      : [];
  }, [marketPrices]);

  const downloadScreenshot = () => {
    takeScreenshot(snapshotRef.current).then((image: any) => {
      const link = document.createElement("a");
      link.href = image;
      link.download = "material_conversion.png";
      link.click();
    });
  };

  const monthlyTickAmount = useMemo(() => {
    return materialConversion?.length
      ? materialConversion?.[0]?.data?.length - 1
      : 11;
  }, [materialConversion]);

  const filteredMaterialConversionData = useMemo(() => {
    return materialConversion;
  }, [materialConversion]);

  const yAxisRange = useMemo(() => {
    if (!materialConversion || materialConversion.length === 0) {
      return { min: 0, max: 100 };
    }

    let maxValue = 0;
    let minValue = Infinity;

    materialConversion.forEach((series: any) => {
      series.data.forEach((point: any) => {
        const value = point[1];
        maxValue = Math.max(maxValue, value);
        minValue = Math.min(minValue, value);
      });
    });

    const minRounded = Math.max(0, Math.floor(minValue / 10) * 10);
    const maxRounded = Math.ceil(maxValue / 10) * 10;

    return {
      min: minRounded,
      max: maxRounded,
    };
  }, [materialConversion]);

  /** Effects */
  useEffect(() => {
    getAllMarketPrices();
  }, []);

  useEffect(() => {
    if (materialOptions?.length && !selectedMaterial) {
      setSelectedMaterial(materialOptions[0].name);
      // @ts-ignore
      setSelectedMaterialId(materialOptions[0]?.id);
    }
  }, [materialOptions]);

  useEffect(() => {
    if (selectedMaterialId) {
      getMaterialConversion(
        startYear,
        startMonth,
        endYear,
        endMonth,
        selectedMaterialId
      );
    }
  }, [startYear, startMonth, endYear, endMonth, selectedMaterialId]);

  return (
    <div className="flex flex-col w-full p-2 sm:p-7 bg-white rounded-xl border-gray-border drop-shadow-none mb-1 sm:mb-10">
      <div className="flex flex-col sm:flex-row w-full items-start justify-between">
        <div className="flex items-center mb-2 sm:mb-12">
          <p className="font-satoshi font-bold text-lg sm:text-2xl text-tailgrids-black">
            Material Conversion (% conversion)
          </p>
          <Tooltip
            content="Non-cumulative: Shows material conversion rates for the selected date range"
            placeholder={undefined}
          >
            <img
              src={InformationIcon}
              alt="info"
              className="inline-block w-4 h-4 ml-2 cursor-pointer"
            />
          </Tooltip>
        </div>
        <div className="flex flex-col sm:flex-row items-center justify-start sm:justify-end gap-4">
          {/* From Date Selection */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-600">From:</span>
            <Menu>
              <MenuHandler>
                <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                  {startYear}
                  <ChevronDown className="ml-2" />
                </button>
              </MenuHandler>
              <MenuList placeholder={undefined}>
                {yearOptions.map((yearOption) => (
                  <MenuItem
                    key={yearOption}
                    disabled={startYear === yearOption}
                    onClick={() => setStartYear(yearOption)}
                    className="font-satoshi text-base text-dark"
                    placeholder={undefined}
                  >
                    {yearOption}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
            <Menu>
              <MenuHandler>
                <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                  {startMonthOptions.find((m) => m.value === startMonth)
                    ?.label || "Month"}
                  <ChevronDown className="ml-2" />
                </button>
              </MenuHandler>
              <MenuList placeholder={undefined}>
                {startMonthOptions.map((monthOption) => (
                  <MenuItem
                    key={monthOption.value}
                    disabled={startMonth === monthOption.value}
                    onClick={() => setStartMonth(monthOption.value)}
                    className="font-satoshi text-base text-dark"
                    placeholder={undefined}
                  >
                    {monthOption.label}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
          </div>

          {/* To Date Selection */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-600">To:</span>
            <Menu>
              <MenuHandler>
                <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                  {endYear}
                  <ChevronDown className="ml-2" />
                </button>
              </MenuHandler>
              <MenuList placeholder={undefined}>
                {yearOptions.map((yearOption) => (
                  <MenuItem
                    key={yearOption}
                    disabled={endYear === yearOption}
                    onClick={() => setEndYear(yearOption)}
                    className="font-satoshi text-base text-dark"
                    placeholder={undefined}
                  >
                    {yearOption}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
            <Menu>
              <MenuHandler>
                <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                  {endMonthOptions.find((m) => m.value === endMonth)?.label ||
                    "Month"}
                  <ChevronDown className="ml-2" />
                </button>
              </MenuHandler>
              <MenuList placeholder={undefined}>
                {endMonthOptions.map((monthOption) => (
                  <MenuItem
                    key={monthOption.value}
                    disabled={endMonth === monthOption.value}
                    onClick={() => setEndMonth(monthOption.value)}
                    className="font-satoshi text-base text-dark"
                    placeholder={undefined}
                  >
                    {monthOption.label}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
          </div>

          <Menu>
            <MenuHandler>
              <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                {selectedMaterial || "Select Material"}
                <ChevronDown className="ml-2" />
              </button>
            </MenuHandler>
            <MenuList placeholder={undefined}>
              {materialOptions.map((material) => (
                <MenuItem
                  key={material.id}
                  disabled={selectedMaterial === material.name}
                  onClick={() => {
                    setSelectedMaterial(material.name);
                    // @ts-ignore
                    setSelectedMaterialId(material?.id);
                  }}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  {material.name}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
          <PrimaryButton
            size="sm"
            onClick={downloadScreenshot}
            label="Download image"
            className="!bg-droppie-oranje !border-droppie-oranje"
          />
        </div>
      </div>
      <div ref={snapshotRef}>
        {/* @ts-ignore */}
        {filteredMaterialConversionData &&
        filteredMaterialConversionData.length > 0 ? (
          <Chart
            options={{
              ...options,
              xaxis: {
                // Only show 1 tick when there's only one month
                tickAmount: monthlyTickAmount,
                labels: {
                  // @ts-ignore
                  formatter: function (value: number) {
                    return dayjs(value).format("MMM");
                  },
                },
              },
              yaxis: {
                ...options.yaxis,
                min: yAxisRange.min,
                max: yAxisRange.max,
              },
            }}
            series={filteredMaterialConversionData}
            height={400}
          />
        ) : (
          <div className="flex items-center justify-center h-[300px] w-full">
            <svg
              aria-hidden="true"
              className="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-primary-base"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
          </div>
        )}
      </div>
    </div>
  );
});

const options = {
  chart: {
    type: "line",
    zoom: {
      enabled: false,
    },
    toolbar: {
      show: false,
    },
  },
  stroke: {
    width: 2,
  },
  dataLabels: {
    enabled: false,
  },
  grid: {
    strokeDashArray: 5,
  },
  markers: {
    size: 1,
  },
  legend: {
    position: "right",
    offsetY: 0,
    height: 400,
    fontSize: "14px",
    fontFamily: "Satoshi, sans-serif",
    labels: {
      colors: "#374151",
    },
    markers: {
      width: 12,
      height: 12,
      strokeWidth: 0,
      radius: 6,
    },
    itemMargin: {
      horizontal: 0,
      vertical: 4,
    },
  },
  tooltip: {
    shared: true,
    intersect: false,
    custom: function ({ series, seriesIndex, dataPointIndex, w }: any) {
      // Create an array of series with their values
      const seriesData = series.map((seriesValues: any, index: number) => ({
        name: w.globals.seriesNames[index],
        value: seriesValues[dataPointIndex],
        color: w.config.colors[index],
      }));

      // Sort by value in descending order (highest to lowest)
      seriesData.sort((a: any, b: any) => b.value - a.value);

      // Build the tooltip HTML with original ApexCharts styling
      let tooltipHtml =
        '<div class="apexcharts-tooltip-title" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">';
      tooltipHtml += `${dayjs(
        w.globals.seriesX[seriesIndex][dataPointIndex]
      ).format("MMM YYYY")}</div>`;

      seriesData.forEach((item: any) => {
        tooltipHtml += `
          <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 1; display: flex;">
            <span class="apexcharts-tooltip-marker" style="background-color: ${
              item.color
            };"></span>
            <div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">
              <div class="apexcharts-tooltip-y-group">
                <span class="apexcharts-tooltip-text-y-label">${
                  item.name
                }: </span>
                <span class="apexcharts-tooltip-text-y-value">${item.value.toFixed(
                  2
                )}%</span>
              </div>
            </div>
          </div>
        `;
      });

      return tooltipHtml;
    },
  },
  yaxis: {
    title: {
      text: "% Conversion",
      style: {
        fontSize: "14px",
        fontWeight: "bold",
      },
    },
    labels: {
      formatter: function (value: number) {
        return `${Math.round(value)}%`;
      },
    },
  },
  colors: [
    "#23C16B", // Green
    "#FFB323", // Yellow
    "#48A7F8", // Blue
    "#FF3333", // Red
    "#FF8800", // Orange
    "#9259FF", // Purple
    "#FF4DA6", // Pink
    "#33FFE6", // Teal
    "#808080", // Gray
    "#D17A50", // Brown
    "#FF7F50", // Coral
    "#8A2BE2", // BlueViolet
    "#5F9EA0", // CadetBlue
    "#DC143C", // Crimson
    "#00FFFF", // Cyan
    "#FF1493", // DeepPink
    "#696969", // DimGray
    "#B8860B", // DarkGoldenrod
    "#FF4500", // OrangeRed
    "#32CD32", // LimeGreen
    "#4682B4", // SteelBlue
    "#00FA9A", // MediumSpringGreen
    "#FFD700", // Gold
    "#FF6347", // Tomato
    "#7B68EE", // MediumSlateBlue
    "#008080", // Teal
    "#6A5ACD", // SlateBlue
    "#D2691E", // Chocolate
    "#B22222", // FireBrick
    "#9ACD32", // YellowGreen
    "#8B008B", // DarkMagenta
    "#00CED1", // DarkTurquoise
    "#FF69B4", // HotPink
    "#1E90FF", // DodgerBlue
    "#40E0D0", // Turquoise
    "#EE82EE", // Violet
  ],
};
